import React, { useState } from "react";
import { notification } from "antd";
import { FoCard, FoChips, FoDialog, FoEmpty, FoKpis, FoTable, formatEgp, statusBadge } from "./financeUi";
import { os } from "./osClient";
import { SelectField, TextField, useOsResource } from "./osForm";
import { ACCOUNT_GROUPS, asList } from "./osRules";

const blank = { code: "", name: "", group: "Assets", balance: "0" };

export default function ChartOfAccounts() {
  const [group, setGroup] = useState("All");
  const [search, setSearch] = useState("");
  const [applied, setApplied] = useState({ group: "All", search: "" });
  const [draft, setDraft] = useState(blank);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const { token, data, loading, reload } = useOsResource(
    (auth) => os.accounts(auth, {
      search: applied.search,
      group: applied.group === "All" ? undefined : applied.group,
    }),
    [applied.search, applied.group]
  );

  const accounts = asList(data, ["accounts"]);
  const totals = data?.groupTotals || {};

  const save = async () => {
    if (!draft.name || !draft.group || Number.isNaN(Number(draft.balance))) {
      notification.error({ message: "Name, group, and balance are required." });
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await os.updateAccount(token, editing._id, {
          name: draft.name,
          group: draft.group,
          balance: Number(draft.balance),
        });
      } else {
        if (!draft.code) {
          notification.error({ message: "A code is required for a custom account." });
          setSaving(false);
          return;
        }
        await os.createAccount(token, {
          code: draft.code,
          name: draft.name,
          group: draft.group,
          balance: Number(draft.balance),
        });
      }
      notification.success({ message: editing ? "Account updated." : "Account created." });
      setOpen(false);
      setEditing(null);
      setDraft(blank);
      reload();
    } catch (err) {
      /* toast already shown */
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fo-stack">
      <FoKpis
        items={ACCOUNT_GROUPS.map((name) => ({
          label: name,
          value: `${formatEgp(totals[name])} EGP`,
        }))}
      />
      <div className="fo-tools">
        <FoChips options={["All", ...ACCOUNT_GROUPS]} value={group} onChange={setGroup} />
        <label className="fo-search-inline">
          <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search code or name" aria-label="Search accounts" />
        </label>
        <button type="button" className="fo-btn" onClick={() => setApplied({ group, search: search.trim() })}>Apply</button>
        <button type="button" className="fo-btn fo-btn-primary" onClick={() => { setEditing(null); setDraft(blank); setOpen(true); }}>New account</button>
      </div>
      <FoCard title="Chart of accounts" note="System accounts are recalculated from the books. Their balances are not editable.">
        {loading && <FoEmpty>Loading accounts…</FoEmpty>}
        {!loading && (
          <FoTable
            rowKey="_id"
            dataSource={accounts}
            pagination={{ pageSize: 20, total: accounts.length }}
            empty="No accounts match this view."
            columns={[
              { title: "Code", dataIndex: "code", render: (code) => <span className="fo-mono">{code}</span> },
              { title: "Name", dataIndex: "name" },
              { title: "Group", dataIndex: "group", render: (value) => statusBadge(value) },
              { title: "Balance", dataIndex: "balance", render: (value) => `${formatEgp(value)} EGP` },
              { title: "Source", dataIndex: "system", render: (system) => statusBadge(system ? "System" : "Custom") },
              {
                title: "",
                key: "actions",
                render: (_, account) => account.system ? null : (
                  <button
                    type="button"
                    className="fo-btn"
                    onClick={() => {
                      setEditing(account);
                      setDraft({
                        code: account.code,
                        name: account.name,
                        group: account.group,
                        balance: String(account.balance ?? 0),
                      });
                      setOpen(true);
                    }}
                  >
                    Edit
                  </button>
                ),
              },
            ]}
          />
        )}
      </FoCard>
      <FoDialog open={open} title={editing ? "Edit account" : "New account"} onClose={() => !saving && setOpen(false)}>
        <div className="fo-form-grid">
          <TextField label="Code" value={draft.code} onChange={(code) => setDraft({ ...draft, code })} hint={editing ? "Code stays on the account." : "System codes are rejected."} />
          <TextField label="Name" value={draft.name} onChange={(name) => setDraft({ ...draft, name })} />
          <SelectField label="Group" value={draft.group} onChange={(groupName) => setDraft({ ...draft, group: groupName })} options={ACCOUNT_GROUPS} />
          <TextField label="Balance" type="number" value={draft.balance} onChange={(balance) => setDraft({ ...draft, balance })} />
        </div>
        <div className="fo-tier-acts" style={{ marginTop: 18 }}>
          <button type="button" className="fo-btn fo-btn-primary" disabled={saving || (editing && editing.system)} onClick={save}>Save</button>
        </div>
      </FoDialog>
    </div>
  );
}
